/**
 * What-If PROPOSAL canvas state (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §4 PROPOSAL row, §6 empty-proposal case, D5).
 *
 * Top to bottom: headline restating the ask (built from the run's form
 * values + scenario name, NOT the backend's headline parts — those exist
 * for provenance/debugging, but the exec-facing sentence is composed
 * client-side per the design doc's exact copy pattern); the controls row
 * (Excel download; plus "← Back to result" in read-only mode) ABOVE the
 * visualization (ruled 2026-08-22: controls must not sit below a
 * scrollable visualization where they need scrolling to discover);
 * per-input visualization (chart for time_series, DA-style before/after
 * grid otherwise — InputDeltaGrid, ruled 2026-08-22); the honesty strip.
 *
 * NO changeText subheader (ruled 2026-08-22): echoing what the user
 * typed doesn't earn its vertical space — the Control Bar below the
 * canvas shows the live form, always visible.
 *
 * NO ACTION FOOTER HERE (2026-08-21 adversarial UI review). Approve /
 * Discard — and the "Nothing is applied until you approve" caution,
 * which belongs next to the control it cautions about — moved into the
 * Control Bar (WhatIfView wires them), so the surface never shows two
 * competing primary CTAs and content is never pinned across a void to
 * the viewport bottom. The short-proposal void is now filled by the real
 * fix (chart for time series; InputDeltaGrid's DA-style before/after
 * table for non-time-series), not layout tricks.
 *
 * Empty-proposal honesty (§6): when `total_changed_rows === 0`, the
 * visualization area is replaced with the calm "didn't match any data"
 * line; WhatIfView offers only Discard in the bar — approving a no-op
 * recalculation is pointless and the copy is explicit that "the diff
 * generator returns sas_url: None on identical JSONs."
 *
 * `readOnly` (design doc §11.2 "View approved changes"): renders this
 * same proposal payload for a completed run with a single "← Back to
 * result" link instead of live actions. Body content is identical
 * read-only material either way.
 */
import React from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { CloudDownload } from '@mui/icons-material';

import { CHART_FRAME_SX } from 'src/components/AgenticV2/cards/results/charts/chartFrame';

import { InputDeltaInput, WhatIfProposal } from '../types';
import { YearWindow } from '../execYearWindow';

import InputDeltaChart from './InputDeltaChart';
import InputDeltaGrid from './InputDeltaGrid';
import HonestyStrip from './HonestyStrip';

interface ProposalStateProps {
  proposal: WhatIfProposal;
  // Client-composed headline inputs — the input/output names come from
  // the form's curated-option lookups (WhatIfView owns those lists),
  // not from the backend's headline parts.
  inputName: string;
  scenarioName: string;
  // Calm inline failure copy for a failed approve/discard call (§6) —
  // the proposal stays on screen so the executive can retry. The
  // actions themselves live in the Control Bar; their failure renders
  // here because an Alert inside the bar would break its fixed band.
  actionError?: string | null;
  // §11.2 read-only mode — see file header.
  readOnly?: boolean;
  // Only used when readOnly — the "← Back to result" affordance.
  onBackToResult?: () => void;
  // Exec AI year window (C1): clips the time-series chart's x-axis to the
  // instance's window; absent/null bounds = show all years.
  yearWindow?: YearWindow | null;
}

/**
 * Shared approvability rule — also consumed by WhatIfView to decide the
 * Control Bar's Approve button state, so the two can never disagree.
 *
 * isEmpty vs input_delta_unavailable are different failure modes and
 * must not be conflated: isEmpty means the patch matched nothing (§6) —
 * there is genuinely nothing to approve. input_delta_unavailable means
 * the patch DID apply but the before/after preview blob couldn't be
 * shown (A5/F7) — the proposal is otherwise real, and when the blob
 * download fails `total_changed_rows` falls back to 0, which makes
 * isEmpty true even though real changes exist. Hence the gate is
 * `(!isEmpty || unavailable)`, never `!isEmpty` alone. `approvable` is
 * the backend-authoritative write-count check (2026-08-21): a run where
 * every patch failed to apply must not be approvable; undefined (older
 * stored proposals) is treated as approvable so reopened runs aren't
 * retroactively blocked.
 */
export const canApproveProposal = (proposal: WhatIfProposal): boolean => {
  const isEmpty = !proposal.input_delta?.total_changed_rows;
  const unavailable = !!proposal.input_delta_unavailable;
  const approvable = proposal.approvable !== false;
  return (!isEmpty || unavailable) && approvable;
};

const ProposalState: React.FC<ProposalStateProps> = ({
  proposal,
  inputName,
  scenarioName,
  actionError = null,
  readOnly = false,
  onBackToResult,
  yearWindow,
}) => {
  // Defensive against a degraded/partial payload — total_changed_rows
  // missing or zero both read as "nothing changed" (§6 empty-proposal
  // treatment), never a crash on a missing field.
  const isEmpty = !proposal.input_delta?.total_changed_rows;
  // F7/A5: the propose pipeline's before/after sandbox blob download
  // failed or was empty — distinct from isEmpty (which means "loaded
  // fine, nothing changed"). Checked first below: an unavailable delta
  // takes priority over the empty-proposal treatment, since we can't
  // know whether anything actually changed without the real data.
  const inputDeltaUnavailable = !!proposal.input_delta_unavailable;
  const inputs = proposal.input_delta?.inputs ?? [];
  const honesty = proposal.honesty ?? [];
  const unattempted = proposal.unattempted ?? [];

  // No independent scroll container here — WhatIfView's canvas region
  // (the immediate parent) is already the single `overflowY: auto` box.
  // Per the amended design doc §4 (ruled 2026-08-20 at final review):
  // the proposal "must fit the viewport whole in the single-input
  // case — chart height compresses when multiple inputs render, and a
  // multi-input proposal may scroll the canvas internally as a
  // fallback" — the Control Bar itself never scrolls (D10).
  return (
    <Box sx={{ flex: 1, minHeight: 0 }}>
      {/* h5, not h4 (2026-08-22 mark 1: "this font is really big") — a
          per-run restatement doesn't outrank the surface's own headings.
          ResultState's headline matches for state-to-state parity.
          mb 0.5 (2026-08-23, screenshot -21): the controls row belongs
          to the headline block — the small gap goes above it, the
          separating gap below it. */}
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        If {inputName} changes — proposed changes to {scenarioName}.
      </Typography>

      {/* Controls row ABOVE the visualization (ruled 2026-08-22 — below
          a scrollable grid they needed scrolling to discover). Same
          text-button · separator idiom as ResultState's row. A null
          diff_xlsx_url means a no-op or degraded diff — never render a
          dead download link (same href/target/rel pattern as the analyst
          Gate 2 diff download, EditScenariosChangeReviewBody.tsx). */}
      {(readOnly || proposal.diff_xlsx_url) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {readOnly && (
            // No arrow icon — its row-mate "Download" carries an icon
            // but the sibling navigation link "View approved changes"
            // (ResultState) has none; the two navigation links must
            // match each other (ruled 2026-08-22). ml -5px on the row's
            // FIRST button = the text-button's own horizontal padding,
            // so its label aligns flush with the headline (screenshot
            // -17; same treatment as ResultState's row).
            <Button
              variant="text"
              size="small"
              onClick={onBackToResult}
              sx={{ ml: '-5px' }}
            >
              Back to result
            </Button>
          )}
          {readOnly && proposal.diff_xlsx_url && (
            <Typography variant="body2" color="text.secondary">
              ·
            </Typography>
          )}
          {proposal.diff_xlsx_url && (
            <Button
              variant="text"
              size="small"
              startIcon={<CloudDownload fontSize="small" />}
              href={proposal.diff_xlsx_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ml: readOnly ? undefined : '-5px' }}
            >
              Download (Excel)
            </Button>
          )}
        </Box>
      )}

      {inputDeltaUnavailable ? (
        // F7/A5: the before/after data itself couldn't be loaded — say
        // so plainly rather than rendering an empty-but-normal-looking
        // visualization. Honesty strip/Excel download are unaffected
        // (spec §4): the patches were still applied and can still be
        // approved, only the before/after preview is missing.
        <Alert severity="info" sx={{ my: 4, maxWidth: 560 }}>
          The before/after view isn&apos;t available for this run.
        </Alert>
      ) : isEmpty ? (
        <Typography variant="body1" color="text.secondary" sx={{ my: 4 }}>
          The requested change didn&apos;t match any data in this model.
        </Typography>
      ) : (
        <>
          {inputs.map((input: InputDeltaInput) =>
            input.kind === 'time_series' ? (
              // Exec chart frame (2026-09-07 ruling): the same outlined frame as the Tornado card and the
              // result chart, its edges in line with the control bar's content edges.
              <Box key={input.input_name} sx={{ ...CHART_FRAME_SX, mb: 2 }}>
                <InputDeltaChart
                  input={input}
                  compact={inputs.length > 1}
                  yearWindow={yearWindow}
                />
              </Box>
            ) : (
              // Non-time-series inputs render the DA-style before/after
              // grid (ruled 2026-08-22: the table replaces the interim
              // computed summary — see InputDeltaGrid's header for the
              // design and its fork lineage).
              <InputDeltaGrid
                key={input.input_name}
                input={input}
                // 2026-08-22 mark 2: with a single input, its name is
                // already in the headline and the Control Bar — the
                // section subtitle is pure repetition. Multi-input
                // proposals still need it to group the blocks.
                showInputName={inputs.length > 1}
              />
            )
          )}
        </>
      )}

      {(!isEmpty || inputDeltaUnavailable || proposal.approvable === false) && (
        <HonestyStrip honesty={honesty} unattempted={unattempted} />
      )}

      {!readOnly && actionError && (
        <Alert severity="info" sx={{ maxWidth: 560, mb: 2 }}>
          {actionError}
        </Alert>
      )}
    </Box>
  );
};

export default ProposalState;
